import './App.css';
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
// Components
import Login from './Components/Pages/Auth/Login';
import Register from './Components/Pages/Auth/Register';
import ForgotPword from './Components/Pages/Auth/ForgotPword';
import EmailVerify from './Components/Pages/Auth/EmailVerify';
import LandingPage from './Components/Pages/Auth/LandingPage';
import PricingPage from './Components/Pages/Auth/PricingPage';
import FeaturesPage from './Components/Pages/Auth/FeaturesPage';
import ContactPage from './Components/Pages/Auth/ContactPage';


import EmailVerifyComplete from './Components/Pages/Auth/EmailVerifyComplete';
import OTPVerify from './Components/Pages/Auth/OTPVerify';
import ResetPassword from './Components/Pages/Auth/ResetPassword';
import PwordResetSucces from './Components/Pages/Auth/PwordResetSucces';
import Dashboard from './Components/Screens/User/Dashboard';
import ScheduledMedia from './Components/Screens/User/ScheduledMedia';
import Media from './Components/Screens/User/Media';
import AddPhoto from './Components/Screens/User/AddPhoto';
import AddMedia from './Components/Screens/User/AddMedia';
import RecipientPage from './Components/Screens/User/Recipient';
import AddRecipient from './Components/Screens/User/AddRecipient';
import RecipientDetails from './Components/Screens/User/RecipientDetails';
import Legacy from './Components/Screens/User/Legacy';
import UserSettings from './Components/Screens/User/Settings';
import UpgradeStorage from './Components/Screens/User/Upgrade';
import AddCard from './Components/Screens/User/AddPlan';
import EditRecipient from './Components/Screens/User/EditRecipient';
import SharedMedia from './Components/Screens/User/SharedMedia';
import MediaGroups from './Components/Screens/User/GroupMedia';
import SuspendedAccount from './Components/Screens/User/SuspendedAccount';
import SubscriptionGuard from './Components/Guards/SubscriptionGuard';

// Admin components
import AdminLayout from './Components/Admin/components/AdminLayout';
import AdminDashboard from './Components/Admin/AdminDashboard';
import UsersList from './Components/Admin/UserList';
import CreateUserForm from './Components/Admin/components/CreateUserForm';
import MediaManagement from './Components/Admin/MediaManagement';
import UserDetails from './Components/Admin/UserDetails';
import PlansManagement from './Components/Admin/PlansManagement';
import EmailCampaign from './Components/Admin/EmailCampaign';
import CreateCampaign from './Components/Admin/CreateEmailCampaign';
import EditCampaign from './Components/Admin/CampaignDetails';
import Reports from './Components/Admin/Reports';
import AdminProfile from './Components/Admin/AdminProfile';
import AccessCodes from './Components/Admin/AccessCodes';
import ForgotPassword from './Components/Pages/Auth/ForgotPassword';
import SubscriptionStatusService from './services/SubscriptionStatusService';
import { login } from './reducer/loginSlice';
import ReceivedMedia from './Components/Screens/User/ReceivedMedia';
import configs from './configs';

function App() {
  const userLoginData = useSelector((state: any) => state.data.login.value);
  const isAuthenticated = !!userLoginData?.token;
  const isAdmin = isAuthenticated &&
    (userLoginData?.data?.user_data?.role === 'superadmin' ||
      userLoginData?.data?.user_data?.role === 'admin');
  // Suspension only applies while payments are enabled.
  const isAccountSuspended = configs.paymentsEnabled && userLoginData?.accountSuspended;
  const navigate = useNavigate();

  // Initialize subscription monitoring as soon as app loads
  useEffect(() => {
    if (isAuthenticated) {
      SubscriptionStatusService.getInstance().startMonitoring();
    }

    return () => {
      SubscriptionStatusService.getInstance().stopMonitoring();
    };
  }, [isAuthenticated]);

  // Authentication guard component with suspension check
  const RequireAuth = ({ children }: { children: JSX.Element }) => {
    if (!isAuthenticated) {
      return <Navigate to="/sign-in" />;
    }

    // If account is suspended, redirect to upgrade page
    if (isAccountSuspended) {
      return <Navigate to="/suspended" />;
    }

    return children;
  };

  // Admin guard component
  const RequireAdmin = ({ children }: { children: JSX.Element }) => {
    return isAdmin ? children : <Navigate to="/dashboard" />;
  };

  return (
    <div>
      <Routes>
        {/* Public Routes - No authentication required */}
        <Route path="/" element={<Login />} />
        <Route path="/pricing" element={configs.paymentsEnabled ? <PricingPage /> : <Navigate to="/" replace />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/contact-us" element={<ContactPage />} />

        {/* Auth Routes */}
        <Route path="/sign-up" element={<Register />} />
        <Route path="/register" element={<Register />} />
        <Route path="/sign-in" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/email-verify" element={<EmailVerify />} />
        <Route path="/verify-email/:email" element={<EmailVerifyComplete />} />
        <Route path="/otp-verify" element={<OTPVerify />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reset-success" element={<PwordResetSucces />} />

        {/* Suspended Account Page */}
        <Route path="/suspended" element={configs.paymentsEnabled ? <SuspendedAccount /> : <Navigate to="/dashboard" replace />} />

        {/* User Routes - Protected with SubscriptionGuard */}
        <Route path="/add-photo" element={
          <RequireAuth>
            <SubscriptionGuard>
              <AddPhoto />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/add-media" element={
          <RequireAuth>
            <SubscriptionGuard>
              <AddMedia />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/dashboard" element={
          <RequireAuth>
            <SubscriptionGuard>
              <Dashboard />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/my-media" element={
          <RequireAuth>
            <SubscriptionGuard>
              <Media />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/shared-media" element={
          <RequireAuth>
            <SubscriptionGuard>
              <SharedMedia />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/group-media" element={
          <RequireAuth>
            <SubscriptionGuard>
              <MediaGroups />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/recipient" element={
          <RequireAuth>
            <SubscriptionGuard>
              <RecipientPage />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/recipients/:id" element={
          <RequireAuth>
            <SubscriptionGuard>
              <RecipientDetails />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/add-recipient" element={
          <RequireAuth>
            <SubscriptionGuard>
              <AddRecipient />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/edit-recipient/:id" element={
          <RequireAuth>
            <SubscriptionGuard>
              <EditRecipient />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/legacy" element={
          <RequireAuth>
            <SubscriptionGuard>
              <Legacy />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        <Route path="/user-settings" element={
          <RequireAuth>
            <SubscriptionGuard>
              <UserSettings />
            </SubscriptionGuard>
          </RequireAuth>
        } />
        {/* Upgrade routes - Not protected by SubscriptionGuard */}
        <Route path="/upgrade" element={configs.paymentsEnabled ? <UpgradeStorage /> : <Navigate to="/dashboard" replace />} />
        <Route path="/add-card" element={
          <RequireAuth>
            {configs.paymentsEnabled ? <AddCard /> : <Navigate to="/dashboard" replace />}
          </RequireAuth>
        } />
        <Route path="/scheduled-media" element={
          <RequireAuth>
            <SubscriptionGuard>
              <ScheduledMedia />
            </SubscriptionGuard>
          </RequireAuth>
        } />

        <Route path="/received-media" element={
          <RequireAuth>
            <SubscriptionGuard>
              <ReceivedMedia />
            </SubscriptionGuard>
          </RequireAuth>
        } />

        {/* Admin Routes - Protected by admin check */}
        <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
          <Route index element={<AdminDashboard />} />

          {/* User management routes */}
          <Route path="users">
            <Route index element={<UsersList />} />
            <Route path="create" element={<CreateUserForm />} />
            <Route path=":id" element={<UserDetails />} />
          </Route>

          <Route path="email-campaigns">
            <Route index element={<EmailCampaign />} />
            <Route path="create" element={<CreateCampaign />} />
            <Route path=":id" element={<EditCampaign />} />
          </Route>

          {/* Other admin routes */}
          <Route path="access-codes" element={<AccessCodes />} />
          <Route path="plans" element={<PlansManagement />} />
          <Route path="media" element={<MediaManagement />} />
          <Route path="reports" element={<Reports />} />
          <Route path="profile" element={<AdminProfile />} />
        </Route>

        {/* 404 catch-all route - Fixed to show a proper 404 page instead of admin dashboard */}
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    </div>
  );
}

// Make sure to add this default export
export default App;